/**
 * @file PurchaseOrderModal.js
 * @version 2.25.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-03
 * @modified 2026-10-30
 *
 * 2.25.0 (2026-10-29, Cam — "PO modal needs to show linked PO-H when no SO-D
 * linking the two … on PO-H edit user should be given option to inform haulage
 * supplier of change via resending"): a stock or self-billed PO's PO-H is a
 * link (its number opens it, its status beside it, "not sent yet" with a Send
 * button while the haulier has not had it) and is in Linked documents; the
 * PO-H's own window names the PO it collects ("Collects PO-D… from <supplier>",
 * opening it). After the PO-H's Edit, one never sent is offered to the haulier
 * ("send it now?"), as a sent one is offered its revised copy; a PO-H raised
 * again from here is offered to send too. Only a click on the backdrop itself
 * closes the window now: the offers' own dialogs are portals whose clicks
 * bubbled up through the backdrop's handler, so "Not now" closed the PO
 * window and "Send" took the send window down with it.
 *
 * 2.24.0 (2026-10-29, Cam): a stock PO collected by a third party
 * (utils/stockHaulage) shows its haulage order as a self-billed PO does —
 * "Collected by <haulier> — PO-H…", or "… archived — raise it again".
 *
 * 2.23.1 (2026-10-28, N6): on a rep's phone a line block's cost says what it
 * is per too ("Cost £400.00 per tonne", "per pallet" on a haulage line) — the
 * one rule, utils/unitPriceLabel through repOrderCard.perUnitText.
 * 2.23.0 (2026-10-28, Cam — units clear on every price): each line's unit
 * cost says what it is per ("£400.00/T", "£10.00/25kg bag" with its figure
 * a tonne, "unit not set"; a Direct Delivery haulage line "/pallet") —
 * PriceWithUnit.
 * 2.22.0 (2026-10-28, Cam — rep mobile review): on a rep's phone the lines
 * (goods and a PO-H's haulage) are blocks (RepLineBlock) instead of the
 * nine-column table cut off at Vehicle; Close stays in view and the
 * details read as label / value rows (RepMobile.css). Desktop unchanged.
 *
 * 2.17.0 (2026-10-13, Cam): a self-billed PO collected by a third party
 * shows its haulage order (PO-H) and haulier under the storage address.
 *
 * 2.16.0 (2026-10-12, Cam): a self-billed PO (utils/selfBilledPo) shows its
 * Delivery / Storage Address (STOCK or the contact) instead of a customer,
 * badged "Self-billed", and our delivery terms beside the supplier's.
 *
 * 2.15.0 (2026-10-09, Cam): Edit on a delivered (or invoiced) order's PO-D
 * opens the PO editor again, for corrections; "Cost & Supplied Qty"
 * (price_change) beside it opens the narrowed window. A PO made in Xero and a
 * PO-H keep the narrowed window on Edit (utils/afterDelivery).
 *
 * 2.11.0 (2026-10-06, Cam): a Supplied column beside the ordered quantity on
 * a PO-D's and a PO-H's lines (the order line's figure — one source of truth —
 * or a stock line's own; none on a Xero-made PO). Edit on a PO made in Xero,
 * or one whose customer order is delivered (`costEdit` from the server), opens
 * the narrowed window (PoCostSuppliedModal): cost price and supplied
 * quantities only.
 *
 * 2.12.0 (2026-10-06, Cam): "Update cost from Xero" in the footer — a PO made
 * in Xero takes its unit costs (only) back from Xero's copy (POST
 * /api/purchase-orders/:id/resync-cost-from-xero), for when Xero holds it as
 * billed and would not take a cost change from here: change it in Xero, then
 * press this. Always in its place; disabled, with the reason, on a PO made
 * here, an archived one, or without costs.view.
 * 2.12.1 (review): when a bill entered here set the cost and Xero's differs,
 * the server asks first (409 NEEDS_CONFIRMATION) — a ConfirmDialog with the
 * bill and both figures, then the same call with confirm.
 * 2.13.0 (2026-10-08, Cam): "Update invoice from supplied qty…" — the changes
 * the invoices of the orders this PO serves (raised, not gone out) still need
 * for their supplied quantities (GET / POST
 * /api/purchase-orders/:id/invoice-proposals). Disabled on a PO made in Xero
 * (it has no supplied quantity).
 * 2.18.0 (2026-10-19, Cam): a PO-H's "Collection & notes" — its collection
 * address per supplier collected from, notes for the haulier (printed) and
 * internal notes (never sent) — HaulagePoDetailsModal; the haulage table
 * shows each line's collection address, marked where changed here; a sent
 * PO-H whose collection or haulier notes changed is offered to the haulier
 * again as a revised PO-H.
 * 2.19.0 (2026-10-19, Cam — "allow the edit button to work in PO-H modal"):
 * Edit on a PO-H opens that window as the PO-H's edit (delivery — Deliver to
 * picker, address, instructions — collection and notes); the separate
 * "Collection & notes" button is gone. A delivered order's PO-H records its
 * supplied quantities through "Cost & Supplied Qty" beside Edit. A PO-H
 * delivering somewhere of its own names it, marked "changed on this PO-H".
 * 2.14.0 (2026-10-08, Cam): a line counted in packs shows its quantity as the
 * PDF prints it — tonnes first ("2 (T)" over "in 500kg totes (4)") or packs
 * first ("4 (500kg totes)" over "2 T"), per the PO's "Show quantities as"
 * (the server's line.qtyDisplay, utils/poQtyDisplay).
 *
 * 2.20.0 (2026-10-20, Cam): the ordered and supplied quantity of every line
 * (PO-H lines' supplied too) show in the line's own unit with the tonnes
 * beneath (PoQtyCell 2.0.0 / QtyWithTonnes), in the order the PO's Show
 * quantities as says.
 * 2.21.0 (2026-10-22, Cam): a PO-H for an order from our stock shows the
 * day the haulier collected it ("Collected", customerOrderCollectedDate)
 * beside its delivery date.
 */

import React, { useState, useEffect, useContext } from 'react';
import UpdateInvoiceFromSuppliedDialog from './UpdateInvoiceFromSuppliedDialog';
import { SearchRevealContext } from '../hooks/useOpenInSearch';
import { XERO_MADE_PO_READ_ONLY, isXeroMadePoId } from '../utils/poOwnership';
import { poEditOpensNarrowed, poCostSuppliedAction, poEditOpensHaulageWindow } from '../utils/afterDelivery';
import { haulageServiceLabel } from '../utils/deliveryTerms';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './PurchaseOrderModal.css';
import { showAlert } from './Alert';
import NonInventoryBadge from './NonInventoryBadge';
import LoadingSpinner from './LoadingSpinner';
import AuditHistoryModal from './AuditHistoryModal';
import LinkedDocuments from './LinkedDocuments';
import SendPOEmailModal from './SendPOEmailModal';
import { formatCurrency, formatPrice } from '../utils/currencyFormatter';
import PriceWithUnit from './PriceWithUnit';
import { formatCount, formatTonnes } from '../utils/numberFormatters';
import { formatDateShort } from '../utils/dateFormatters';
import { formatVatWithRate } from '../utils/taxTypes';
import { formatAddress as formatAddressUtil } from '../utils/formatAddress';
import { useSlideOutButton } from './SlideOutButton';
import { poStatusLabel, poStatusBadgeClass } from '../utils/poStatus';
import useOpenInSearch from '../hooks/useOpenInSearch';
import DocumentOutputModal from './DocumentOutputModal';
import PoCostSuppliedModal from './PoCostSuppliedModal';
import PoQtyCell from './PoQtyCell';
import ConfirmDialog from './ConfirmDialog';
import HaulagePoDetailsModal from './HaulagePoDetailsModal';
import { useAccess } from '../hooks/useAccess';
import XeroVersionDialog from './XeroVersionDialog';
// Divert / Split act on the PO's customer order — the hub (PoHubActions).
import { usePoHub, PoHubBadges, PoHubActions } from './PoHubActions';
import { useEntityModal } from './EntityModalProvider';
import { SELF_BILLED_LABEL, SELF_BILLED_BADGE_TITLE } from '../utils/selfBilledPo';
import useRepMobile from '../hooks/useRepMobile';
import RepLineBlock, { RepLineBlocks } from './RepLineBlock';
import { perUnitText } from '../utils/repOrderCard';

/**
 * Does the catalogue name say anything the line's own description does not?
 *
 * No when they match, and no when the description already contains the name —
 * which covers the "name plus appended detail" case, where repeating the name
 * underneath would be noise. Yes only where the two have genuinely diverged,
 * which is where seeing both actually helps.
 */
/*
 * A PO-H line's facts for its quantity with tonnes (utils/qtyWithTonnes): its
 * own unit weight (stored since 2026-10-08), else weight ÷ quantity — the
 * server's poQtyDisplay.haulageLineUnitWeight rule.
 */
const haulageQtyLine = (l) => {
  const own = Number(l?.unitWeightKg);
  const w = Number(l?.weightKg);
  const q = Number(l?.quantity);
  const kg = own > 0 ? own : (w > 0 && q > 0 ? Math.round((w / q) * 1000) / 1000 : null);
  return { unitWeightKg: kg, pallets: l?.pallets ?? null };
};

const itemNameAdds = (item) => {
  const name = String(item?.itemName || '').trim();
  const desc = String(item?.description || '').trim();
  if (!name || !desc) return false;
  return !desc.toLowerCase().includes(name.toLowerCase());
};

function PurchaseOrderModal({ purchaseOrderId, user, onClose, onEdit, onOpenInSearch }) {
  // A rep's phone: the lines as blocks rather than the table (2.22.0).
  const repMobile = useRepMobile();
  // "Find in …" wherever this was opened from (hooks/useOpenInSearch).
  const { open: openInSearch } = useOpenInSearch('purchase_order', onOpenInSearch, onClose);
  // The PO being emailed to its supplier, straight from this modal.
  const [sendingPO, setSendingPO] = useState(null);
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  // Export (DocumentOutputModal) replaced the header's download-PDF button.
  const [exporting, setExporting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // The narrowed Edit window: cost and supplied only (PoCostSuppliedModal).
  const [costEditing, setCostEditing] = useState(false);
  // "Update cost from Xero" in flight.
  const [resyncing, setResyncing] = useState(false);
  // A bill entered here set the cost and Xero's differs: the question to ask.
  const [resyncConfirm, setResyncConfirm] = useState(null);
  const { can, isAdmin } = useAccess();
  const [comparingXero, setComparingXero] = useState(false);

  // Slideout buttons. `po` and `loading` are deps because the "Find in Search
  // Purchase Orders" footer button only renders once the PO has loaded — with
  // an empty dep array the measure runs on the first render cycle, finds only
  // the Close button, and the later-arriving button stays stuck at its
  // collapsed 45px width.
  useSlideOutButton([po, loading, resyncing]);
  // Update invoice from supplied qty… (2026-10-08).
  const [invoiceUpdate, setInvoiceUpdate] = useState(false);
  // A PO-H's edit window (delivery, collection, notes), and the revised PO-H to offer after it.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [revisedOffer, setRevisedOffer] = useState(null);
  // A PO-H never sent to its haulier: offered after its Edit, or after it is
  // raised again from here (2026-10-29) — { po (SendPOEmailModal's shape), message }.
  const [sendOffer, setSendOffer] = useState(null);
  // A PO-H being sent from this window that is not this window's PO (a stock /
  // self-billed PO's PO-H): sending it leaves this window open.
  const [sendingHaulage, setSendingHaulage] = useState(null);
  // The PO ↔ PO-H link opens the other one on top (EntityModalProvider).
  const { openEntity, available: canOpenEntity } = useEntityModal();
  const appNav = useContext(SearchRevealContext)?.onNavigate;
  const hub = usePoHub(po?.id);

  useEffect(() => {
    if (purchaseOrderId) {
      fetchPurchaseOrderDetails();
      // Prevent background scrolling
      document.body.classList.add('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.add('modal-open');
      }
    }

    return () => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.remove('modal-open');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrderId]);

  const fetchPurchaseOrderDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/purchase-orders/${purchaseOrderId}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setPo(data.purchaseOrder);
      } else {
        showAlert('Failed to load purchase order details');
      }
    } catch (error) {
      console.error('Failed to fetch purchase order:', error);
      showAlert('Failed to load purchase order: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.remove('modal-open');
      }
      onClose();
    }, 300);
  };

  const getStatusBadgeClass = poStatusBadgeClass;

  // Why "Update cost from Xero" cannot be used on this PO, or null.
  const resyncBlock = (() => {
    if (!po) return 'Loading';
    const xid = (po.xeroPurchaseOrderId || po.purchaseOrderID || purchaseOrderId || '').toString();
    if (po.poType === 'haulage' || !(po.xeroMade || isXeroMadePoId(xid))) {
      return 'Only a PO made in Xero takes its cost from Xero — this one was made here, so its cost is changed with Edit';
    }
    if (po.status === 'DELETED' || po.status === 'VOIDED') return `Cannot update — this PO is ${po.status === 'DELETED' ? 'archived' : 'voided'}`;
    if (!can('costs.view')) return 'Taking a cost from Xero needs permission to see costs';
    return null;
  })();

  // Why "Compare with Xero…" cannot be used, or null (admins only — the server checks too).
  const compareBlock = (() => {
    if (!po) return 'Loading';
    const xid = (po.xeroPurchaseOrderId || po.purchaseOrderID || purchaseOrderId || '').toString();
    if (po.poType === 'haulage' || !isXeroMadePoId(xid)) return 'Only a PO made in Xero has a Xero copy to compare';
    if (po.status === 'DELETED' || po.status === 'VOIDED') return `This PO is ${po.status === 'DELETED' ? 'archived' : 'voided'}`;
    if (!po.id) return 'Not saved locally';
    return null;
  })();

  // A self-billed PO whose PO-H was archived by hand: a save never raises it
  // again on its own (selfBilledHaulageService), so it is raised from here.
  const [raisingHaulage, setRaisingHaulage] = useState(false);
  const raiseHaulageAgain = async () => {
    if (raisingHaulage || !po?.id) return;
    setRaisingHaulage(true);
    try {
      const res = await fetch(`${API_URL}/api/purchase-orders/db/${po.id}/raise-haulage-po`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        showAlert(data.error || 'The haulage order could not be raised', 'error');
        return;
      }
      fetchPurchaseOrderDetails();
      // Raised, never sent: offer it to the haulier now (2026-10-29).
      const h = (data.raised || [])[0];
      if (h) {
        setSendOffer({
          po: { purchaseOrderID: h.xero_purchase_order_id, dbId: h.id, purchaseOrderNumber: h.purchase_order_number },
          other: true,
          message: `Haulage order ${h.purchase_order_number} raised to ${h.supplier_name || 'the haulier'} — send it now?`
        });
      } else {
        showAlert('Haulage order raised for the haulier.', 'success');
      }
    } catch (error) {
      showAlert(`The haulage order could not be raised: ${error.message}`, 'error');
    } finally {
      setRaisingHaulage(false);
    }
  };

  /*
   * Collected by a third party — a self-billed PO, or a stock PO on
   * Third-Party Collection (2026-10-29): its PO-H to the haulier, or the one
   * archived by hand, said, and raised again only on purpose. The server gives
   * these only for those two kinds.
   */
  // The other PO of the pair, opened on top; closing it brings this one up to date.
  const openLinkedPo = (link) => {
    const id = link?.xero_purchase_order_id || link?.xeroId;
    if (canOpenEntity && id) openEntity('purchase_order', id, { onClose: () => fetchPurchaseOrderDetails() });
  };
  const linkedPoNumber = (link, title) => (canOpenEntity && (link.xero_purchase_order_id || link.xeroId) ? (
    <button type="button" data-read-safe className="po-linked-doc" onClick={() => openLinkedPo(link)} title={title}>
      {link.purchase_order_number}
    </button>
  ) : <span>{link.purchase_order_number}</span>);
  const linkedStatus = (status) => (status
    ? <span className={`${getStatusBadgeClass(status)} po-linked-status`}>{poStatusLabel(status)}</span> : null);
  const hp = po?.selfBilledHaulagePo;
  const hf = po?.haulageForPo;

  const haulageLink = !po ? null : (
    <>
      {hp && (
        <p className="po-linked-order" title={po.selfBilled ? 'The haulage order raised for this self-billed PO'
          : 'The haulage order raised for this stock PO — the haulier collects from the supplier and brings it to our site'}>
          Collected by {hp.haulier || 'the haulier'} — {linkedPoNumber(hp, `Open ${hp.purchase_order_number}`)}
          {linkedStatus(hp.status)}
          {po.haulageService ? ` · ${haulageServiceLabel(po.haulageService)}` : ''}
          {!hp.sent_to_contact && (
            <>
              <span className="po-linked-unsent"> · not sent yet</span>
              <button
                type="button"
                className="btn-icon"
                data-write-action
                onClick={() => setSendingHaulage({ purchaseOrderID: hp.xero_purchase_order_id, dbId: hp.id, purchaseOrderNumber: hp.purchase_order_number })}
                title={`Send ${hp.purchase_order_number} to ${hp.haulier || 'the haulier'}`}
                aria-label={`Send ${hp.purchase_order_number} to the haulier`}
              >
                <span className="material-symbols-rounded">forward_to_inbox</span>
              </button>
            </>
          )}
        </p>
      )}
      {/* A PO-H raised for a stock or self-billed PO: the PO it collects
          (no customer order between them — 2026-10-29). */}
      {hf && (
        <p className="po-linked-order" title={hf.kind === 'self_billed'
          ? 'The self-billed PO this haulage order collects'
          : 'The stock PO this haulage order collects — from the supplier, to our site'}>
          Collects {linkedPoNumber(hf, `Open ${hf.purchase_order_number}`)}
          {hf.supplier ? ` from ${hf.supplier}` : ''}
          {hf.kind === 'self_billed' ? ' (self-billed)' : ''}
          {linkedStatus(hf.status)}
        </p>
      )}
      {!po.selfBilledHaulagePo && po.selfBilledHaulageHeld && (
        <p className="po-linked-order po-haulage-held">
          {po.selfBilledHaulageHeld.purchase_order_number} archived — raise it again
          {' '}
          <button
            type="button"
            className="btn-icon"
            data-write-action
            onClick={raiseHaulageAgain}
            disabled={raisingHaulage}
            title={`Raise a new haulage order for ${po.selfBilledHaulageHeld.haulier || 'the haulier'}`}
            aria-label="Raise the haulage order again"
          >
            <span className="material-symbols-rounded">{raisingHaulage ? 'progress_activity' : 'local_shipping'}</span>
          </button>
        </p>
      )}
    </>
  );

  // Take Xero's unit costs back onto this PO (only the costs). `confirm`:
  // the person has agreed to replace a cost a bill entered here set.
  const resyncCostFromXero = async (confirm = false) => {
    if (resyncBlock || resyncing) return;
    setResyncConfirm(null);
    setResyncing(true);
    try {
      const id = po.purchaseOrderID || purchaseOrderId;
      const res = await fetch(`${API_URL}/api/purchase-orders/${encodeURIComponent(id)}/resync-cost-from-xero`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(confirm ? { confirm: true } : {})
      });
      const data = await res.json();
      if (!confirm && data.code === 'NEEDS_CONFIRMATION') {
        setResyncConfirm(data.error);
        return;
      }
      if (!res.ok || !data.success) {
        showAlert(data.error || 'The cost could not be taken from Xero', 'error');
        return;
      }
      const warn = (data.warnings || 0) > 0;
      showAlert((data.messages || []).join(' ') || 'Cost updated from Xero.', warn ? 'warning' : 'success');
      fetchPurchaseOrderDetails();
    } catch (error) {
      showAlert(`The cost could not be taken from Xero: ${error.message}`, 'error');
    } finally {
      setResyncing(false);
    }
  };


  const formatReference = (reference) => {
    if (!reference) return null;
    // If reference is a string, return it
    if (typeof reference === 'string') return reference;
    // If reference is an object (shouldn't happen, but defensive), convert to string
    if (typeof reference === 'object') {
      console.warn('Reference is an object, expected string:', reference);
      // If it's an address object, return the first line
      if (reference.addressLine1) return reference.addressLine1;
      return JSON.stringify(reference);
    }
    return String(reference);
  };

  const formatAddress = (address, customerName = null) => {
    return formatAddressUtil(address, { separator: '\n', stripName: customerName, fallback: '-' });
  };

  if (!purchaseOrderId) return null;

  return createPortal(
    /* Only a click on the backdrop itself closes the window: the dialogs this
       window opens (the revised PO-H / send offers) render in portals of their
       own, and React bubbles their clicks up through here — "Not now" on one
       closed the PO window, and "Send" lost the send window with it (2026-10-30). */
    <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div
        className={`modal-content po-modal ${closing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="header-left">
            <h2>{loading ? 'Loading...' : `Purchase Order ${po?.purchaseOrderNumber}`}</h2>
            {!loading && po && (
              <span className={getStatusBadgeClass(po.status)}>
                {poStatusLabel(po.status)}
              </span>
            )}
            {!loading && po && <PoHubBadges hub={hub} onChanged={() => { hub.reload(); fetchPurchaseOrderDetails(); }} />}
            {/* A line here came from a customer order line typed by hand, so it
                carries no inventory item — see NonInventoryBadge. */}
            {!loading && Array.isArray(po?.lineItems) && (
              <NonInventoryBadge
                present={po.lineItems.some(li => li.orderLineId && !li.itemCode)}
                count={po.lineItems.filter(li => li.orderLineId && !li.itemCode).length}
              />
            )}
            {!loading && po && po.sentToContact === true && (
              <span
                className="material-symbols-rounded"
                style={{ fontSize: '18px', color: 'var(--color-success)', cursor: 'help' }}
                title="Sent to supplier"
              >
                mail
              </span>
            )}
            {!loading && po && po.sentToContact === false && (
              <span
                className="material-symbols-rounded"
                style={{ fontSize: '18px', color: 'var(--color-error)', cursor: 'help' }}
                title="Not sent to supplier"
              >
                unsubscribe
              </span>
            )}
            {!loading && po && po.sentToContact === null && (
              <span
                className="material-symbols-rounded"
                style={{ fontSize: '18px', color: 'var(--text-muted)', cursor: 'help' }}
                title="Sent status unknown (not synced)"
              >
                mail
              </span>
            )}
          </div>
          <div className="header-actions">
            {/* Edit: always in its place, disabled with the reason when it
                cannot be used. The server sends the reason (`editBlock`: who
                owns the PO — utils/poOwnership.poChangeRefusal — a haulage
                order, or a billed PO without 'Edit posted documents'); the
                old local rule is only the fallback. */}
            {(onEdit || po?.costEdit || poEditOpensHaulageWindow(po)) && !loading && po && (() => {
              const xid = (po.xeroPurchaseOrderId || purchaseOrderId)?.toString() || '';
              // A PO-H: its own edit window — delivery, collection, notes
              // (Cam, 2026-10-19). Made in Xero: the narrowed window (cost
              // and supplied only — 2026-10-06). A delivered order's PO-D
              // opens the editor (2026-10-09).
              const haulageWindow = poEditOpensHaulageWindow(po);
              const narrowed = poEditOpensNarrowed(po);
              const block = haulageWindow
                ? (po.editBlock || (po.status === 'DELETED' ? 'Cannot edit — it is archived' : null))
                : narrowed ? null : (po.editBlock !== undefined
                ? po.editBlock
                : (po.poType === 'haulage'
                  ? 'A haulage order follows its customer order — edit the order instead'
                  : isXeroMadePoId(xid)
                    ? XERO_MADE_PO_READ_ONLY
                    : null));
              return (
                <button
                  className={`btn-icon${block ? ' btn-disabled' : ''}`}
                  disabled={!!block}
                  onClick={() => {
                    if (block) return;
                    if (haulageWindow) { setDetailsOpen(true); return; }
                    if (narrowed) { setCostEditing(true); return; }
                    handleClose();
                    onEdit(po.xeroPurchaseOrderId || purchaseOrderId);
                  }}
                  title={block || (haulageWindow
                    ? 'Edit — its delivery, collection and notes (its lines follow the order)'
                    : narrowed ? `Edit cost${po.costEdit.supplied ? ' and supplied quantities' : ''} — ${po.costEdit.reason}` : 'Edit')}
                >
                  <span className="material-symbols-rounded">edit</span>
                </button>
              );
            })()}
            {/* Cost & Supplied Qty: a delivered order's PO-D, whose Edit is
                the full editor. Always in its place, disabled with why. */}
            {(onEdit || po?.costEdit || poEditOpensHaulageWindow(po)) && !loading && po && (() => {
              const a = poCostSuppliedAction(po);
              return (
                <button
                  className={`btn-icon${a.enabled ? '' : ' btn-disabled'}`}
                  disabled={!a.enabled}
                  onClick={() => { if (a.enabled) setCostEditing(true); }}
                  title={a.title}
                >
                  <span className="material-symbols-rounded">price_change</span>
                </button>
              );
            })()}
            <button
              className="btn-icon"
              onClick={() => setShowHistory(true)}
              disabled={loading}
              title="View change history"
            >
              <span className="material-symbols-rounded">history</span>
            </button>
            <button
              className="btn-icon"
              data-read-safe
              onClick={() => setExporting(true)}
              disabled={loading || !po?.id}
              title="Export — preview, then print, download or email to yourself"
            >
              <span className="material-symbols-rounded">ios_share</span>
            </button>
            <button className="btn-icon close-btn" onClick={handleClose} title="Close">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="modal-body">
            <LoadingSpinner />
          </div>
        ) : po ? (
          <div className="modal-body">
            <div className="po-main-sections">
              <div className="po-details-section">
                <h3>Order Details</h3>
                <div className="po-details-grid">
                  <div className="detail-row">
                    <span className="label">Date:</span>
                    <span className="value">{formatDateShort(po.date)}</span>
                  </div>
                  {po.deliveryDate && (
                    <div className="detail-row">
                      <span className="label">Delivery Date:</span>
                      <span className="value">{formatDateShort(po.deliveryDate)}</span>
                    </div>
                  )}
                  {/* The haulier collected it from our stock on (its order's
                      Collection / Delivery, 2026-10-22). */}
                  {po.poType === 'haulage' && po.customerOrderCollectedDate && (
                    <div className="detail-row">
                      <span className="label">Collected:</span>
                      <span className="value">{formatDateShort(po.customerOrderCollectedDate)}</span>
                    </div>
                  )}
                  {formatReference(po.reference) && (
                    <div className="detail-row">
                      <span className="label">Reference:</span>
                      <span className="value">{formatReference(po.reference)}</span>
                    </div>
                  )}
                  {po.supplierReference && (
                    <div className="detail-row">
                      <span className="label">Supplier Reference:</span>
                      <span className="value">{po.supplierReference}</span>
                    </div>
                  )}
                  {po.deliveryInstructions && (
                    <div className="detail-row">
                      <span className="label">Delivery Instructions:</span>
                      <span className="value">
                        {po.deliveryInstructions}
                        {po.deliveryOverride?.instructionsOverridden && <span className="po-h-own-delivery"> (changed on this PO-H)</span>}
                      </span>
                    </div>
                  )}
                  {po.poType === 'haulage' && po.haulierNotes && (
                    <div className="detail-row">
                      <span className="label">Notes for the haulier:</span>
                      <span className="value" style={{ whiteSpace: 'pre-line' }}>{po.haulierNotes}</span>
                    </div>
                  )}
                  {po.poType === 'haulage' && po.internalNotes && (
                    <div className="detail-row" title="Internal — never printed or sent to the haulier">
                      <span className="label po-note-internal">Internal notes:</span>
                      <span className="value" style={{ whiteSpace: 'pre-line' }}>{po.internalNotes}</span>
                    </div>
                  )}
                  {po.supplierDeliveryTerms && (
                    <div className="detail-row">
                      <span className="label">Supplier Delivery Terms:</span>
                      <span className="value" style={{ fontWeight: '600' }}>{po.supplierDeliveryTerms}</span>
                    </div>
                  )}
                  {/* Self-billed: our terms are kept on the PO (it has no order). */}
                  {po.selfBilled && (
                    <div className="detail-row">
                      <span className="label">Our Delivery Terms:</span>
                      <span className="value" style={{ fontWeight: '600' }}>{po.ourDeliveryTerms || '—'}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="supplier-section">
                <h3>Supplier</h3>
                <div className="address-block">
                  <p className="contact-name">{po.contact?.name || po.contactName || '-'}</p>
                  {po.supplierAddress && (
                    <>
                      {po.supplierAddressIsInvoice && (
                        <span style={{
                          display: 'inline-block',
                          margin: '6px 0 -2px -3px',
                          padding: '2px 7px',
                          fontSize: '0.7em',
                          textTransform: 'uppercase',
                          letterSpacing: '0.2px',
                          fontWeight: 600,
                          color: 'var(--bg-main)',
                          background: 'rgba(var(--text-secondary-rgb), 0.6)',
                          borderRadius: 'var(--radius-xl)'
                        }}>Invoice Address</span>
                      )}
                      <p style={{ whiteSpace: 'pre-line', marginTop: '4px', color: 'var(--text-secondary)' }}>
                        {formatAddress(po.supplierAddress)}
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div className="customer-section">
                <h3>{po.selfBilled ? 'Delivery / Storage Address' : 'Customer / Delivery To'}</h3>
                <div className="address-block">
                  {po.selfBilled ? (
                    /* Self-billed: no customer — where the goods go (utils/selfBilledPo). */
                    <>
                      <p className="contact-name">
                        {po.deliveryTo === 'stock' ? 'Stock — our own site' : (po.deliveryContactName || '—')}
                        <span className="status-badge status-self-billed" title={SELF_BILLED_BADGE_TITLE}>{SELF_BILLED_LABEL}</span>
                      </p>
                      {po.deliveryAddress && (
                        <p style={{ whiteSpace: 'pre-line', marginTop: '4px', color: 'var(--text-secondary)' }}>
                          {formatAddress(po.deliveryAddress, po.deliveryTo === 'stock' ? 'Excel Agri Ltd.' : po.deliveryContactName)}
                        </p>
                      )}
                      {haulageLink}
                    </>
                  ) : po.customerName ? (
                    <>
                      {/* A PO-H delivering somewhere of its own (its edit
                          window): who it goes to, and that the order's
                          customer is unchanged. */}
                      <p className="contact-name">{po.deliveryOverride?.toOverridden ? (po.deliveryOverride.name || po.customerName) : po.customerName}</p>
                      {po.deliveryOverride?.toOverridden && (
                        <p className="po-h-own-delivery" title="Set in this PO-H's Edit — the order is unchanged">
                          Delivery changed on this PO-H — for {po.customerName}
                        </p>
                      )}
                      {/* What this PO was bought FOR. The modal named the
                          customer but never the order, so the one link people
                          wanted from a purchase order was not there. */}
                      {po.customerOrderNumber && (
                        <p className="po-linked-order">{po.customerOrderNumber}</p>
                      )}
                      {po.deliveryAddress && (
                        <p style={{ whiteSpace: 'pre-line', marginTop: '4px', color: 'var(--text-secondary)' }}>
                          {formatAddress(po.deliveryAddress, po.deliveryOverride?.toOverridden ? po.deliveryOverride.name : po.customerName)}
                        </p>
                      )}
                      {/* A Xero-made stock PO keeps a customer's name from Xero
                          and may still be collected by a third party. */}
                      {haulageLink}
                    </>
                  ) : (
                    <>
                      <p className="contact-name">Stock</p>
                      {po.deliveryAddress && (
                        <p style={{ whiteSpace: 'pre-line', marginTop: '4px', color: 'var(--text-secondary)' }}>
                          {formatAddress(po.deliveryAddress)}
                        </p>
                      )}
                      {/* A stock PO collected by a third party: its PO-H (2026-10-29). */}
                      {haulageLink}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* The customer order(s) this PO buys for, their invoices and the
                bills on this PO — the shared panel the order, invoice and bill
                modals carry too. Bill amounts are hidden by the server for a
                user without costs.view, as every PO figure is. */}
            <LinkedDocuments type="purchase_order" id={po.id} refreshKey={po} onChange={fetchPurchaseOrderDetails} />

            {po.poType === 'haulage' ? (
            /* PO-H: what the haulier moves. No prices — see
               services/haulagePoService.js on the server. */
            <div className="line-items-section">
              <h3>
                Haulage
                {po.haulageService && (
                  <span className="po-h-service" title="The service the haulier is booked for">
                    {' '}· {haulageServiceLabel(po.haulageService)}{po.haulageService === 'economy' ? ' — no fixed date' : ''}
                  </span>
                )}
              </h3>
              {repMobile ? (
                <RepLineBlocks empty="No lines">
                  {po.haulageLines.map((l, index) => (
                    <RepLineBlock
                      key={index}
                      code={l.itemCode}
                      name={l.description || '—'}
                      facts={[
                        { key: 'qty', label: 'Quantity', value: <PoQtyCell quantity={l.quantity} line={haulageQtyLine(l)} mode={po.qtyDisplay} unitsOnly /> },
                        { key: 'supplied', label: 'Supplied', value: l.suppliedQuantity != null ? <PoQtyCell quantity={l.suppliedQuantity} line={haulageQtyLine(l)} mode={po.qtyDisplay} supplied /> : null, none: 'Not yet' },
                        { key: 'weight', label: 'Weight', value: l.weightKg ? formatTonnes(l.weightKg / 1000) : null },
                        { key: 'pallets', label: 'Pallets', align: 'right', value: l.pallets ? formatCount(l.pallets) : null }
                      ]}
                    >
                      {(l.collectFrom || l.collectDate) && (
                        <div className="rep-line-foot">
                          <span>
                            Collect from <strong>{l.collectFrom || '—'}</strong>
                            {l.collectFromAddress ? `, ${l.collectFromAddress}` : ''}
                          </span>
                          {l.collectDate && <span>{`${formatDateShort(l.collectDate)}${l.supplyPoNumber ? ` · ${l.supplyPoNumber}` : ''}`}</span>}
                        </div>
                      )}
                    </RepLineBlock>
                  ))}
                </RepLineBlocks>
              ) : (
              <table className="line-items-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Quantity</th>
                    <th className="text-right" title="What was supplied — recorded on the customer order">Supplied</th>
                    <th className="text-right">Weight</th>
                    <th className="text-right">Pallets</th>
                    <th>Collect From</th>
                  </tr>
                </thead>
                <tbody>
                  {po.haulageLines.length > 0 ? po.haulageLines.map((l, index) => (
                    <tr key={index}>
                      <td>
                        <span className="item-line-head">
                          {l.itemCode && <span className="item-code-badge">{l.itemCode}</span>}
                          <span className="item-line-name">{l.description || '—'}</span>
                        </span>
                      </td>
                      <td>
                        {/* Its Weight and Pallets columns say the rest. */}
                        <PoQtyCell quantity={l.quantity} line={haulageQtyLine(l)} mode={po.qtyDisplay} unitsOnly />
                      </td>
                      <td className="text-right" title={l.suppliedBlock || undefined}>
                        {l.suppliedQuantity != null ? <PoQtyCell quantity={l.suppliedQuantity} line={haulageQtyLine(l)} mode={po.qtyDisplay} supplied /> : '-'}
                      </td>
                      <td className="text-right">{l.weightKg ? formatTonnes(l.weightKg / 1000) : '-'}</td>
                      <td className="text-right">{l.pallets ? formatCount(l.pallets) : '-'}</td>
                      <td>
                        {l.collectFrom || '-'}
                        {l.collectOverridden && <span className="collect-changed" title="Changed here in Collection & notes"> (changed)</span>}
                        {l.collectFromAddress && <span className="collect-address">{l.collectFromAddress}</span>}
                        {l.collectDate && (
                          <span className="contract-ref">
                            {` Collect ${formatDateShort(l.collectDate)}${l.supplyPoNumber ? ` · ${l.supplyPoNumber}` : ''}`}
                          </span>
                        )}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="6" className="no-items">No lines</td>
                    </tr>
                  )}
                </tbody>
              </table>
              )}
            </div>
            ) : (
            <>
            <div className="line-items-section">
              <h3>Line Items</h3>
              {repMobile ? (
                <RepLineBlocks empty="No line items">
                  {(po.lineItems || []).map((item, index) => {
                    const transport = [item.haulier, item.driver, item.vehicle].filter(Boolean).join(' · ');
                    return (
                      <RepLineBlock
                        key={index}
                        code={item.itemCode}
                        name={item.description || item.itemName || '—'}
                        words={[itemNameAdds(item) ? item.itemName : '', item.contractRef]}
                        facts={[
                          { key: 'qty', label: 'Quantity', value: <PoQtyCell quantity={item.quantity} line={item} mode={po.qtyDisplay} /> },
                          ...(!po.xeroCostOnly ? [{
                            key: 'supplied',
                            label: 'Supplied',
                            value: item.kind !== 'haulage' && item.suppliedQuantity != null
                              ? <PoQtyCell quantity={item.suppliedQuantity} line={item} mode={po.qtyDisplay} supplied />
                              : null,
                            none: 'Not yet'
                          }] : []),
                          { key: 'amount', label: 'Line total', align: 'right', value: item.lineAmount != null ? <span className="roc-fact-value">{formatCurrency(item.lineAmount)}</span> : null }
                        ]}
                      >
                        <div className="rep-line-foot">
                          <span>{item.unitAmount != null ? <>Cost <strong>{formatPrice(item.unitAmount)}</strong> {item.kind === 'haulage' ? 'per pallet' : perUnitText(item)}</> : null}</span>
                          {transport && <span>{transport}</span>}
                        </div>
                      </RepLineBlock>
                    );
                  })}
                </RepLineBlocks>
              ) : (
              <table className="line-items-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Quantity</th>
                    {!po.xeroCostOnly && <th className="text-right" title="What was supplied (driver / weighbridge) — the bill is checked against it">Supplied</th>}
                    <th>Unit Price</th>
                    <th className="text-right">VAT</th>
                    <th className='po-haulier'>Haulier</th>
                    <th className='po-driver'>Driver</th>
                    <th>Vehicle</th>
                    <th className="text-right">Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {po.lineItems && po.lineItems.length > 0 ? (
                    po.lineItems.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <div className="item-description">
                            {/* THE LINE'S OWN DESCRIPTION LEADS, always.
                                It is the order's own record of what was asked
                                for, it is never absent (checked: 0 of 28,947
                                coded lines lack one), and it is what somebody
                                edited when they appended a contract number, a
                                vet reference or "2 totes classic ewe, 1 tote
                                fast lamb". Leading with the catalogue name
                                instead would quietly rewrite the order: a
                                quarter of lines have a description that does
                                not contain the current item name at all —
                                "KD Milkers Dec 25 Blend (PR-9475)" against an
                                item since renamed "(Davis)".

                                The catalogue name follows only when the
                                description does not already say it, and is
                                labelled as the item's current name so it reads
                                as context rather than as part of the order. */}
                            <span className="item-line-head">
                              {item.itemCode && <span className="item-code-badge">{item.itemCode}</span>}
                              <span className="item-line-name">{item.description || item.itemName || '—'}</span>
                            </span>
                            {itemNameAdds(item) && (
                              <span className="item-line-desc" title="Current name for this item code">
                                {item.itemName}
                              </span>
                            )}
                            {item.contractRef && (
                              <span className="contract-ref" title="The contract this line is drawn against">{item.contractRef}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <PoQtyCell quantity={item.quantity} line={item} mode={po.qtyDisplay} />
                        </td>
                        {!po.xeroCostOnly && (
                          <td className="text-right" title={item.suppliedBlock || undefined}>
                            {item.kind !== 'haulage' && item.suppliedQuantity != null ? <PoQtyCell quantity={item.suppliedQuantity} line={item} mode={po.qtyDisplay} supplied /> : '-'}
                          </td>
                        )}
                        <td>{item.kind === 'haulage'
                          ? `${formatPrice(item.unitAmount)}/pallet`
                          : <PriceWithUnit value={item.unitAmount} line={item} />}</td>
                        <td className="text-right">{formatVatWithRate(item.taxAmount, item.taxType, formatCurrency)}</td>
                        <td className='po-haulier'>{item.haulier || '-'}</td>
                        <td className='po-driver'>{item.driver || '-'}</td>
                        <td>{item.vehicle || '-'}</td>
                        <td className="text-right">{formatCurrency(item.lineAmount)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={po.xeroCostOnly ? 8 : 9} className="no-items">No line items</td>
                    </tr>
                  )}
                </tbody>
              </table>
              )}
            </div>

            <div className="totals-section">
              <div className="totals-grid">
                <div className="total-row">
                  <span className="label">Subtotal:</span>
                  <span className="value">{formatCurrency(po.subTotal)}</span>
                </div>
                <div className="total-row">
                  <span className="label">Tax Total:</span>
                  <span className="value">{formatCurrency(po.totalTax)}</span>
                </div>
                <div className="total-row total">
                  <span className="label">Total:</span>
                  <span className="value">{formatCurrency(po.total)}</span>
                </div>
              </div>
            </div>
            </>
            )}
          </div>
        ) : (
          <div className="modal-body">
            <div className="error-state">
              Failed to load purchase order details
            </div>
          </div>
        )}

        {/* Offered only where the host screen can navigate — hands the user to
            Search Purchase Orders pre-filtered to this PO so they can take any
            action available there. Absent for every other caller, and with
            Close now in the header it is the only thing the footer holds, so
            the footer goes with it rather than sitting there empty. */}
        {po && (
          <div className="modal-footer">
            {/* The action people come here for once they have checked the PO
                reads right. One PO, so it goes straight to the email modal. */}
            <button
              className="btn-slideout"
              onClick={() => setSendingPO(po)}
              disabled={po.status === 'DELETED'}
              title={po.status === 'DELETED' ? 'Cannot send — this PO is deleted' : 'Send to Supplier'}
              data-write-action
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">forward_to_inbox</span>
              </div>
              <span className="btn-label">Send to Supplier</span>
            </button>
            <PoHubActions hub={hub} user={user} onChanged={fetchPurchaseOrderDetails} />
            {/* Xero holds a PO as billed and will not take a cost change from
                here: change it in Xero, then take it back with this. */}
            <button
              className="btn-slideout"
              onClick={() => resyncCostFromXero(false)}
              disabled={!!resyncBlock || resyncing}
              title={resyncBlock || 'Take this PO\'s unit costs from Xero — after changing them on the PO in Xero (only the cost changes here; quantities and words stay as they are)'}
              data-write-action
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">sync</span>
              </div>
              <span className="btn-label">{resyncing ? 'Reading Xero…' : 'Update cost from Xero'}</span>
            </button>
            {/* Admins: compare our copy with Xero's and, where they differ,
                accept Xero's version (Cam, 2026-10-20 — XeroVersionDialog). */}
            {isAdmin && (
              <button
                className="btn-slideout"
                onClick={() => setComparingXero(true)}
                disabled={!!compareBlock}
                title={compareBlock || 'Read Xero\'s copy now and, if it differs, accept Xero\'s version over ours'}
                data-write-action
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">compare_arrows</span>
                </div>
                <span className="btn-label">Compare with Xero…</span>
              </button>
            )}
            <button
              className="btn-slideout"
              onClick={() => setInvoiceUpdate(true)}
              disabled={!!po.xeroCostOnly}
              title={po.xeroCostOnly
                ? 'A PO made in Xero has no supplied quantity of its own while the Xero PO sync is on'
                : 'The changes its customer order\'s invoice still needs for the supplied quantities (until the invoice has gone out)'}
              data-write-action
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">receipt_long</span>
              </div>
              <span className="btn-label">Update invoice from supplied qty…</span>
            </button>
            {openInSearch && <button
              className="btn-slideout"
              onClick={() => { openInSearch(po); handleClose(); }}
              title="Open this purchase order in Search Purchase Orders"
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">manage_search</span>
              </div>
              <span className="btn-label">Find in Search POs</span>
            </button>}
          </div>
        )}

        {comparingXero && po?.id && (
          <XeroVersionDialog
            poId={po.id}
            poNumber={po.purchaseOrderNumber}
            onClose={() => setComparingXero(false)}
            onDone={() => fetchPurchaseOrderDetails()}
          />
        )}
        {sendingPO && (
          <SendPOEmailModal
            purchaseOrder={sendingPO}
            user={user}
            onClose={() => setSendingPO(null)}
            onSent={() => { setSendingPO(null); handleClose(); }}
          />
        )}
      </div>

      {detailsOpen && po && (
        <HaulagePoDetailsModal
          poId={po.id}
          onClose={() => { setDetailsOpen(false); fetchPurchaseOrderDetails(); }}
          onSaved={(out) => {
            setDetailsOpen(false);
            fetchPurchaseOrderDetails();
            if (out.revised) setRevisedOffer(out.revised);
            else if (out.send_offer) {
              // Never sent to its haulier (a stock / self-billed PO's PO-H): offer it now.
              setSendOffer({
                po: null,
                message: `${out.changed ? `${out.purchase_order_number} saved. ` : ''}It has not been sent to ${out.send_offer.haulier || 'the haulier'} yet — send it now?`
              });
            } else showAlert(out.changed ? `${out.purchase_order_number} saved` : 'Nothing changed', 'success');
          }}
        />
      )}
      <ConfirmDialog
        isOpen={!!revisedOffer}
        title="Send the revised PO-H?"
        type="info"
        message={revisedOffer ? `${revisedOffer.purchase_order_number} was sent to ${revisedOffer.haulier || 'the haulier'}: its ${revisedOffer.changes.map(c => c.replace(/ changed$/, '')).join(' and ')} changed. Send them the revised PO-H now?` : ''}
        confirmText="Send revised PO-H"
        confirmIcon="forward_to_inbox"
        onConfirm={() => {
          const r = revisedOffer;
          setRevisedOffer(null);
          if (po) setSendingPO({ ...po, revision: { summary: 'amended', changes: r.changes } });
        }}
        onCancel={() => setRevisedOffer(null)}
      />
      {/* A PO-H the haulier has never had (2026-10-29): Send / Not now. */}
      <ConfirmDialog
        isOpen={!!sendOffer}
        title="Send the PO-H?"
        type="info"
        message={sendOffer?.message || ''}
        confirmText="Send PO-H"
        confirmIcon="forward_to_inbox"
        cancelText="Not now"
        onConfirm={() => {
          const o = sendOffer;
          setSendOffer(null);
          if (o?.other) setSendingHaulage(o.po);
          else if (po) setSendingPO({ ...po });
        }}
        onCancel={() => setSendOffer(null)}
      />
      {sendingHaulage && (
        <SendPOEmailModal
          purchaseOrder={sendingHaulage}
          user={user}
          onClose={() => { setSendingHaulage(null); fetchPurchaseOrderDetails(); }}
          onSent={() => { setSendingHaulage(null); fetchPurchaseOrderDetails(); }}
        />
      )}

      {/* Audit History Modal */}
      <AuditHistoryModal
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        entityType="purchase_order"
        entityId={po?.id}
        entityIdentifier={po?.purchaseOrderNumber}
      />

      {invoiceUpdate && po && (
        <UpdateInvoiceFromSuppliedDialog
          baseUrl={`${API_URL}/api/purchase-orders/${encodeURIComponent(po.purchaseOrderID || purchaseOrderId)}/invoice-proposals`}
          onClose={() => setInvoiceUpdate(false)}
          onDone={(message, type) => showAlert(message, type || 'success')}
          onNavigate={appNav ? (menu) => { handleClose(); appNav(menu); } : null}
        />
      )}

      {costEditing && po && (
        <PoCostSuppliedModal
          purchaseOrderId={po.purchaseOrderID || purchaseOrderId}
          onFullEdit={onEdit ? (id) => { handleClose(); onEdit(id); } : undefined}
          onClose={() => { setCostEditing(false); fetchPurchaseOrderDetails(); }}
          onSaved={(r) => showAlert(r?.xero?.ok && r.cost_lines_changed
            ? `Saved, and the new cost${r.cost_lines_changed === 1 ? ' is' : 's are'} in Xero.` : 'Saved.', 'success')}
        />
      )}

      <ConfirmDialog
        isOpen={!!resyncConfirm}
        title="Take the cost from Xero?"
        message={resyncConfirm || ''}
        confirmText="Take Xero's figure"
        cancelText="Keep the bill's"
        onConfirm={() => resyncCostFromXero(true)}
        onCancel={() => setResyncConfirm(null)}
      />

      {exporting && po?.id && (
        <DocumentOutputModal
          type="purchase_order"
          ids={[po.id]}
          onClose={() => setExporting(false)}
          onDone={(message) => { setExporting(false); if (message) showAlert(message, 'success'); }}
        />
      )}
    </div>,
    document.body
  );
}

export default PurchaseOrderModal;
